import React, { useCallback, useMemo, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { printResponsibleSummary } from '../print/printResponsibleSummary';
import { api } from '../../services/api';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

export function ResponsibleQueryModule({
  assignments = [],
  materials = [],
  people = [],
  warehouses = [
    { id: 'popayan', name: 'Popayán' },
    { id: 'santander', name: 'Santander de Quilichao' },
  ],
  dotPrefixOnly = false,
}) {
  const [pendingDoc, setPendingDoc] = useState('');
  const [selectedDoc, setSelectedDoc] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [extraAssignments, setExtraAssignments] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [movementFilter, setMovementFilter] = useState('all'); // all | asignacion | prestamo
  const [peopleSource, setPeopleSource] = useState(people || []);
  const [warehousesSource, setWarehousesSource] = useState(warehouses || []);
  const [movementAssigned, setMovementAssigned] = useState([]);

  const mergedAssignments = useMemo(() => {
    const byId = new Map();
    [...(assignments || []), ...(extraAssignments || [])].forEach(a => {
      const k = String(a.id || `${a.materialId}-${a.responsibleDoc}-${a.warehouseId}-${a.assignDate}`);
      if (!byId.has(k)) byId.set(k, a);
    });
    return Array.from(byId.values());
  }, [assignments, extraAssignments]);

  const activeAssignments = useMemo(
    () => (mergedAssignments || []).filter((a) => {
      if (a.status !== 'Asignado') return false;
      return true;
    }),
    [mergedAssignments]
  );

  const resolveDocToDni = useMemo(() => {
    const peopleArr = Array.isArray(peopleSource) ? peopleSource : [];
    const dniSet = new Set(peopleArr.map(p => String(p.dni || p.id)));
    const byDbId = new Map(peopleArr.map(p => [String(p._dbId ?? ''), String(p.dni || p.id)]));
    const byId = new Map(peopleArr.map(p => [String(p.id ?? ''), String(p.dni || p.id)]));
    const byCedula = new Map(peopleArr.map(p => [String(p.cedula ?? ''), String(p.dni || p.id)]));
    return (doc) => {
      const raw = String(doc || '');
      if (!raw) return raw;
      const s = raw.replace(/[^0-9]/g, ''); // prefer digits when vienen mezclados
      if (dniSet.has(raw)) return raw;
      if (dniSet.has(s)) return s;
      const m1 = byDbId.get(raw) || byDbId.get(s);
      if (m1) return m1;
      const m2 = byId.get(raw) || byId.get(s);
      if (m2) return m2;
      const m3 = byCedula.get(raw) || byCedula.get(s);
      if (m3) return m3;
      return raw || s;
    };
  }, [peopleSource]);

  const [extraByDoc, setExtraByDoc] = useState({});

  const responsibles = useMemo(() => {
    const byId = new Map();

    (peopleSource || []).forEach((p) => {
      const id = String(p.dni || p.id).trim();
      if (!id) return;
      const name = String(p.name || '').trim() || id;
      byId.set(id, name);
    });

    const docsFromAssignments = Array.from(
      new Set(
        (activeAssignments || [])
          .map((a) => String(resolveDocToDni(a.responsibleDoc || '')).trim())
          .filter(Boolean)
      )
    );

    const byDocAssign = new Map(
      (activeAssignments || []).map((a) => [
        String(resolveDocToDni(a.responsibleDoc || '')).trim(),
        String(a.responsibleName || ''),
      ])
    );

    docsFromAssignments.forEach((doc) => {
      if (byId.has(doc)) return;
      const name =
        byDocAssign.get(doc) ||
        (extraByDoc[doc]?.name || '') ||
        doc;
      byId.set(doc, name);
    });

    const list = Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
    return list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [peopleSource, activeAssignments, resolveDocToDni, extraByDoc]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const docs = Array.from(new Set((activeAssignments || []).map(a => String(resolveDocToDni(a.responsibleDoc || '')).trim()).filter(Boolean)));
        const byDni = new Map((peopleSource || []).map(p => [String(p.dni || p.id), String(p.name || '')]));
        const missing = docs.filter(d => {
          const nm = byDni.get(d);
          return !nm || nm.trim() === '' || nm === d;
        });
        if (!missing.length) return;
        const fetched = {};
        await Promise.all(missing.map(async (dni) => {
          try {
            const r = await api.people.get({ dni, limit: 1 });
            const dataRaw = Array.isArray(r?.data) ? r.data[0] : (Array.isArray(r) ? r[0] : (r?.data || null));
            if (dataRaw) fetched[dni] = { name: dataRaw.name || dataRaw.nombre || '', dni };
          } catch (_) {}
        }));
        if (!cancelled && Object.keys(fetched).length) {
          setExtraByDoc(prev => ({ ...prev, ...fetched }));
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [activeAssignments, peopleSource, resolveDocToDni]);

  useEffect(() => {
    setPeopleSource(people || []);
  }, [people]);

  useEffect(() => {
    setWarehousesSource(warehouses || []);
  }, [warehouses]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!selectedDoc || String(selectedDoc) === 'all') { setExtraAssignments([]); return; }
        const dni = String(selectedDoc);
        const page = 1000;
        let acc = [];
        for (let off = 0; ; off += page) {
          const resp = await api.assignments.get({ dni, limit: page, offset: off, order: 'asc' });
          const rows = Array.isArray(resp?.data) ? resp.data : [];
          if (rows.length === 0) break;
          acc = acc.concat(rows.map(a => ({
            id: a.id,
            materialId: a.material_id,
            materialName: a.material_name || '',
            materialCode: a.material_code || '',
            materialCategory: a.material_category || a.materialCategory || '',
            brandItemId: a.brand_item_id ?? null,
            serial: String(a.serial || '').trim(),
            responsibleDoc: a.person_dni || a.person_id,
            responsibleName: a.person_name || String(a.person_dni || a.person_id || ''),
            quantity: a.quantity,
            assignDate: a.created_at ? String(a.created_at).slice(0, 10) : new Date().toISOString().slice(0, 10),
            hasPeripherals: 'No',
            status: 'Asignado',
            observations: a.notes || '',
            custodyVehicleId: (() => {
              const m = String(a.notes || '').match(/\[custodia_vehiculo:(\d+)\]/);
              return m ? Number(m[1]) : 0;
            })(),
            evidencePhotos: [],
            warehouseId: a.warehouse_id || ''
          })));
          if (rows.length < page) break;
        }
        try {
          const personRow = (peopleSource || []).find((p) => String(resolveDocToDni(p?.dni || p?.id)) === dni);
          const personDbId = Number(personRow?._dbId || personRow?.id || 0);
          if (personDbId > 0) {
            const vRes = await api.vehicleAssignments.get();
            const vehList = Array.isArray(vRes?.data) ? vRes.data : [];
            for (const v of vehList) {
              const vid = Number(v?.id || 0);
              if (!vid) continue;
              const activeForPerson = (v.current_assignments || []).filter(
                (va) => String(va?.status || '') === 'Activo' && Number(va?.person_id || 0) === personDbId
              );
              if (!activeForPerson.length) continue;
              const dotRes = await api.vehicleDotacion.get({ vehicle_id: vid });
              const dotRows = Array.isArray(dotRes?.data) ? dotRes.data : [];
              dotRows.forEach((dr, idx) => {
                const mid = Number(dr?.material_id || 0);
                const wid = String(dr?.warehouse_id || '').trim();
                const serial = String(dr?.serial || '').trim();
                const dupKey = `${mid}|${normalizeWarehouseId(wid)}|${serial}`;
                const exists = acc.some((a) => {
                  const ak = `${Number(a.materialId)}|${normalizeWarehouseId(a.warehouseId)}|${String(a.serial || '').trim()}`;
                  return ak === dupKey;
                });
                if (exists) return;
                acc.push({
                  id: `veh-dot-${vid}-${idx}-${mid}`,
                  materialId: mid,
                  materialName: dr.material_name || dr.material_code || '',
                  materialCode: dr.material_code || '',
                  materialCategory: dr.dotacion_category || '',
                  brandItemId: dr.brand_item_id ?? null,
                  serial,
                  responsibleDoc: dni,
                  responsibleName: personRow?.name || dni,
                  quantity: Number(dr.quantity) || 1,
                  assignDate: dr.assigned_at ? String(dr.assigned_at).slice(0, 10) : new Date().toISOString().slice(0, 10),
                  hasPeripherals: serial ? 'Sí' : 'No',
                  status: 'Asignado',
                  observations: `Dotación vehículo ${v.plate || vid}`,
                  custodyVehicleId: vid,
                  evidencePhotos: [],
                  warehouseId: wid,
                });
              });
            }
          }
        } catch (_) {}
        if (!cancelled) setExtraAssignments(acc);
      } catch (_) {
        if (!cancelled) setExtraAssignments([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDoc, peopleSource, resolveDocToDni]);

  useEffect(() => {
    if ((peopleSource || []).length) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.people.get({ limit: 1000, clean: 1 });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows.map((p) => ({
          id: p.id,
          _dbId: p._dbId ?? null,
          dni: p.dni || p.cedula || p.documento_identidad || p.documento || p.doc || p.id || '',
          name: p.name || p.nombre || p.nombre_completo || p.fullname || '',
          status: p.status || 'Activo',
        }));
        if (!cancelled && mapped.length) {
          setPeopleSource(mapped);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [peopleSource]);

  const normalizeWarehouseId = (v) => {
    const s = String(v || '').toLowerCase().trim();
    const noAcc = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const slug = noAcc.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (/^popayan/.test(slug)) return 'popayan';
    if (/^santander/.test(slug) || /quilichao/.test(slug)) return 'santander';
    if (/^(c054|lv).*centro/.test(slug)) return 'lv-centro';
    if (/^(c086|lv).*norte/.test(slug)) return 'lv-norte';
    return slug || s;
  };

  useEffect(() => {
    if ((warehousesSource || []).length) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.warehouses.list({ limit: 200, order: 'asc', include_deleted: 1 });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const list = rows.map((w) => {
          const id = normalizeWarehouseId(w.id || w.code || w.name);
          const name = String(w.name || id || '').trim() || id;
          return { id, name };
        });
        const seen = new Set();
        const unique = list
          .filter((w) => {
            const k = String(w.id);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          })
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        if (!cancelled && unique.length) {
          setWarehousesSource(unique);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [warehousesSource]);

  const warehouseNameById = useMemo(() => {
    const map = new Map();
    (warehousesSource || []).forEach((w) => {
      const id = String(w.id);
      if (!id) return;
      map.set(id, String(w.name || id));
    });
    return map;
  }, [warehousesSource]);

  const normalizeText = useCallback((v) =>
    String(v || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim(), []);

  const materialById = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((m) => map.set(m.id, m));
    return map;
  }, [materials]);

  const typeOptions = useMemo(() => {
    const map = new Map();
    (materials || []).forEach((m) => {
      const raw = String(m.category || m.type || '').trim();
      if (!raw) return;
      const norm = normalizeText(raw);
      if (!norm || map.has(norm)) return;
      map.set(norm, raw);
    });
    (assignments || []).forEach((a) => {
      const raw = String(a.materialCategory || a.material_category || '').trim();
      if (!raw) return;
      const norm = normalizeText(raw);
      if (!norm || map.has(norm)) return;
      map.set(norm, raw);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [materials, assignments, normalizeText]);

  const hasDotPrefix = useCallback((assignment) => {
    const mat = materialById.get(assignment.materialId);
    const codeRaw = String(assignment.materialCode || assignment.material_code || mat?.code || '').trim();
    if (!codeRaw) return false;
    const codeNorm = codeRaw.toUpperCase().replace(/\s+/g, '');
    return codeNorm.startsWith('DOT');
  }, [materialById]);

  const results = useMemo(() => {
    if (!selectedDoc) return [];
    if (String(selectedDoc) !== 'all' && Array.isArray(movementAssigned)) {
      let list = movementAssigned;
      if (warehouseFilter !== 'all') {
        list = list.filter((a) => {
          const widNorm = normalizeWarehouseId(a.warehouseId || '');
          return widNorm === warehouseFilter;
        });
      }
      if (typeFilter !== 'all') {
        const wanted = normalizeText(typeFilter);
        list = list.filter((a) => {
          const mat = materialById.get(a.materialId);
          const raw =
            mat?.category ||
            mat?.type ||
            a.materialCategory ||
            a.material_category ||
            '';
          const norm = normalizeText(raw);
          if (!norm) return false;
          return norm === wanted || norm.includes(wanted);
        });
      }
      if (dotPrefixOnly) {
        list = list.filter(hasDotPrefix);
      }
      return list;
    }

    let list = activeAssignments.map(a => ({ ...a, _src: 'assignment', type: a.type || 'Asignación' }));
    if (String(selectedDoc) !== 'all') {
      list = list.filter(
        (a) => String(resolveDocToDni(a.responsibleDoc)) === String(selectedDoc)
      );
    }
    if (warehouseFilter !== 'all') {
      list = list.filter((a) => {
        const widNorm = normalizeWarehouseId(a.warehouseId || '');
        return widNorm === warehouseFilter;
      });
    }
    if (typeFilter !== 'all') {
      const wanted = normalizeText(typeFilter);
      list = list.filter((a) => {
        const mat = materialById.get(a.materialId);
        const raw =
          mat?.category ||
          mat?.type ||
          a.materialCategory ||
          a.material_category ||
          '';
        const norm = normalizeText(raw);
        if (!norm) return false;
        return norm === wanted || norm.includes(wanted);
      });
    }
    if (dotPrefixOnly) {
      list = list.filter(hasDotPrefix);
    }
    return list;
  }, [activeAssignments, selectedDoc, warehouseFilter, typeFilter, resolveDocToDni, materialById, dotPrefixOnly, normalizeText, movementAssigned, hasDotPrefix]);

  const [movementLoans, setMovementLoans] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!selectedDoc) { setMovementLoans([]); return; }
        const dni = resolveDocToDni(selectedDoc);
        const now = new Date();
        const start = new Date(now.getTime() - 365 * 24 * 3600 * 1000);
        const fmt = (d) => {
          const yyyy = d.getFullYear(), mm = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };
        const start_date = fmt(start);
        const end_date = fmt(now);

        const page = 1000;
        const maxPages = 30;
        let movs = [];
        for (let p = 0; p < maxPages; p += 1) {
          const off = p * page;
          const resp = await api.movements.get({ dni, start_date, end_date, order: 'asc', limit: page, offset: off });
          const rows = Array.isArray(resp?.data) ? resp.data : (resp?.data ? [resp.data] : []);
          if (rows.length === 0) break;
          movs = movs.concat(rows);
          if (rows.length < page) break;
        }

        // Determinar préstamos abiertos: último movimiento por brand_item_id o por (material_id+serial)
        const lastByKey = new Map();
        movs.forEach((mv) => {
          const mid = Number(mv?.material_id);
          const bid = mv?.brand_item_id ?? mv?.brandItemId ?? null;
          const serial = String(mv?.serial || '').trim();
          if (!mid) return;
          const key = bid ? `bid:${bid}` : (serial ? `ser:${mid}:${serial}` : `mat:${mid}:${mv?.id}`);
          const prev = lastByKey.get(key);
          if (!prev || Number(prev?.id) < Number(mv?.id)) lastByKey.set(key, mv);
        });

        const openLoans = [];
        for (const mv of lastByKey.values()) {
          const t = String(mv?.type || '').toLowerCase();
          if (t !== 'prestamo') continue;
          const widNorm = normalizeWarehouseId(mv?.warehouse_id || '');
          if (warehouseFilter !== 'all' && widNorm !== warehouseFilter) continue;
          openLoans.push({
            id: `mv-${mv.id}`,
            materialId: Number(mv.material_id),
            materialName: mv.material_name || '',
            materialCode: mv.material_code || '',
            materialCategory: mv.material_category || '',
            serial: String(mv.serial || '').trim(),
            brandName: String(mv.brand || '').trim(),
            responsibleDoc: selectedDoc,
            responsibleName: mv.requester_name || '',
            quantity: Number(mv.quantity) || 1,
            assignDate: String(mv.created_at || '').slice(0, 10),
            hasPeripherals: String(mv.serial || '').trim() ? 'Sí' : 'No',
            status: 'Asignado',
            observations: mv.notes || '',
            evidencePhotos: [],
            warehouseId: mv.warehouse_id || '',
            _src: 'movement',
            type: 'Préstamo'
          });
        }
        if (!cancelled) setMovementLoans(openLoans);
      } catch (_) {
        if (!cancelled) setMovementLoans([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDoc, warehouseFilter, resolveDocToDni]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!selectedDoc || String(selectedDoc) === 'all') { setMovementAssigned([]); return; }
        const dni = resolveDocToDni(selectedDoc);
        const now = new Date();
        const start = new Date(now.getTime() - 5 * 365 * 24 * 3600 * 1000);
        const fmt = (d) => {
          const yyyy = d.getFullYear(), mm = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };
        const start_date = fmt(start);
        const end_date = fmt(now);

        const page = 1000;
        const maxPages = 30;
        let movs = [];
        for (let p = 0; p < maxPages; p += 1) {
          const off = p * page;
          const resp = await api.movements.get({ dni, start_date, end_date, order: 'asc', limit: page, offset: off });
          const rows = Array.isArray(resp?.data) ? resp.data : (resp?.data ? [resp.data] : []);
          if (rows.length === 0) break;
          movs = movs.concat(rows);
          if (rows.length < page) break;
        }
        if (cancelled) return;

        const normalizeMovType = (raw) => {
          let t = String(raw || '').toLowerCase().trim();
          if (t === 'asignación') t = 'asignacion';
          if (t === 'devolución') t = 'devolucion';
          if (t === 'préstamo') t = 'prestamo';
          if (t === 'consumible') t = 'consumible';
          return t;
        };

        const net = new Map();
        const lastAssign = new Map();
        movs.forEach((mv) => {
          const t = normalizeMovType(mv?.type);
          if (t !== 'asignacion' && t !== 'devolucion' && t !== 'consumible' && t !== 'salida') return;
          const mid = Number(mv?.material_id);
          if (!mid) return;
          const widNorm = normalizeWarehouseId(mv?.warehouse_id || '');
          if (!widNorm) return;
          const serial = String(mv?.serial || '').trim();
          const key = serial ? `ser:${mid}:${widNorm}:${serial}` : `mat:${mid}:${widNorm}`;
          const qty = Number(mv?.quantity) || 0;
          if (qty <= 0) return;
          const prev = net.get(key) || 0;
          if (t === 'asignacion') {
            net.set(key, prev + qty);
            lastAssign.set(key, mv);
          } else {
            const next = Math.max(0, prev - qty);
            net.set(key, next);
          }
        });

        const out = [];
        for (const [key, qty] of net.entries()) {
          if (!qty || qty <= 0) continue;
          const mv = lastAssign.get(key);
          if (!mv) continue;
          const mid = Number(mv?.material_id);
          const wid = mv?.warehouse_id || '';
          const serial = String(mv?.serial || '').trim();
          const mat = materialById.get(mid);
          out.push({
            id: `net-${key}`,
            materialId: mid,
            materialName: mv?.material_name || mat?.name || '',
            materialCode: mv?.material_code || mat?.code || '',
            materialCategory: mv?.material_category || mat?.category || mat?.type || '',
            serial,
            brandName: String(mv?.brand || '').trim(),
            responsibleDoc: selectedDoc,
            responsibleName: mv?.requester_name || '',
            quantity: qty,
            assignDate: String(mv?.created_at || '').slice(0, 10),
            hasPeripherals: serial ? 'Sí' : 'No',
            status: 'Asignado',
            observations: mv?.notes || '',
            evidencePhotos: [],
            warehouseId: wid,
            _src: 'movement_net',
            type: 'Asignación'
          });
        }

        if (!cancelled) setMovementAssigned(out);
      } catch (_) {
        if (!cancelled) setMovementAssigned([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDoc, resolveDocToDni, materialById]);

  const selectedResponsible = useMemo(() => {
    if (!selectedDoc) return null;
    const found = responsibles.find((r) => String(r.id) === String(selectedDoc)) || null;
    if (found && (!found.name || found.name === found.id)) {
      const extra = extraByDoc[String(selectedDoc)];
      if (extra?.name) return { ...found, name: extra.name };
    }
    return found;
  }, [responsibles, selectedDoc, extraByDoc]);

  const totals = useMemo(() => {
    const acc = new Map();
    results.forEach((r) => {
      const key = `${r.materialId}`;
      const prev = acc.get(key) || { quantity: 0, materialName: r.materialName };
      acc.set(key, { quantity: prev.quantity + (parseInt(r.quantity) || 0), materialName: prev.materialName });
    });
    return Array.from(acc.entries()).map(([mid, info]) => ({ materialId: mid, ...info }));
  }, [results]);

  // Enriquecer resultados con serie y marca a partir de movimientos y serials
  const [serialPoolByGroup, setSerialPoolByGroup] = useState({});
  const [brandByGroupSerial, setBrandByGroupSerial] = useState({});
  const [assignedBy, setAssignedBy] = useState('');
  
  useEffect(() => {
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        if (!selectedDoc || String(selectedDoc) === 'all') {
          if (!cancelled) {
            setSerialPoolByGroup({});
            setBrandByGroupSerial({});
            setAssignedBy('');
          }
          return;
        }

        const dni = resolveDocToDni(selectedDoc);
        const dates = Array.from(new Set((results || []).map((r) => String(r.assignDate || '').slice(0, 10)).filter(Boolean))).sort();
        if (dates.length === 0) {
          if (!cancelled) {
            setSerialPoolByGroup({});
            setBrandByGroupSerial({});
            setAssignedBy('');
          }
          return;
        }

        const start_date = dates[0];
        const end_date = dates[dates.length - 1];

        const page = 1000;
        const maxPages = 30;
        let movs = [];
        for (let p = 0; p < maxPages; p += 1) {
          const off = p * page;
          const movsResp = await api.movements.get({ dni, start_date, end_date, order: 'asc', limit: page, offset: off });
          const rows = Array.isArray(movsResp?.data) ? movsResp.data : (movsResp?.data ? [movsResp.data] : []);
          if (rows.length === 0) break;
          movs = movs.concat(rows);
          if (rows.length < page) break;
        }
        if (cancelled) return;

        const pool = {};
        const brandMap = {};
        let performedBy = '';

        movs.forEach((mv) => {
          const type = String(mv.type || '').toLowerCase();
          if (!['asignacion', 'prestamo'].includes(type) && !type.includes('asignaci')) return;

          const mid = Number(mv?.material_id);
          if (!mid) return;

          const widNorm = normalizeWarehouseId(mv?.warehouse_id || '');
          if (warehouseFilter !== 'all' && widNorm !== warehouseFilter) return;

          const date = String(mv?.created_at || '').slice(0, 10);
          if (!date) return;

          const serial = String(mv?.serial || '').trim();
          if (!serial) return;

          if (!performedBy && (type === 'asignacion' || type.includes('asignaci'))) {
            performedBy = String(mv?.performed_by_name || mv?.performed_by_email || '').trim();
          }

          const key = `${dni}|${mid}|${widNorm}|${date}`;
          if (!pool[key]) pool[key] = [];
          if (!pool[key].includes(serial)) pool[key].push(serial);
          const brand = String(mv?.brand || '').trim();
          if (brand) brandMap[`${key}|${serial}`] = brand;
        });

        if (!cancelled) {
          setSerialPoolByGroup(pool);
          setBrandByGroupSerial(brandMap);
          setAssignedBy(performedBy || '');
        }
      } catch (_) {
        if (!cancelled) {
          setSerialPoolByGroup({});
          setBrandByGroupSerial({});
        }
      }
    }, 500); // Debounce de 500ms

    return () => { 
      cancelled = true; 
      clearTimeout(timeoutId);
    };
  }, [selectedDoc, results, warehouseFilter, resolveDocToDni]);

  // Efecto secundario eliminado/simplificado para evitar duplicidad de llamadas
  // El primer efecto ya debería capturar la mayoría de la info basándose en movimientos históricos.


  const resultsEnriched = useMemo(() => {
    const baseList = (() => {
      if (movementFilter === 'asignacion') return results;
      if (movementFilter === 'prestamo') return movementLoans;
      return [...results, ...movementLoans];
    })();
    const usedByGroup = {};
    return (baseList || []).map((r) => {
      const dni = resolveDocToDni(r.responsibleDoc);
      const mid = Number(r.materialId);
      const widNorm = normalizeWarehouseId(r.warehouseId || '');
      const date = String(r.assignDate || '').slice(0, 10);
      const groupKey = `${dni}|${mid}|${widNorm}|${date}`;

      const pool = Array.isArray(serialPoolByGroup[groupKey]) ? serialPoolByGroup[groupKey] : [];
      const used = usedByGroup[groupKey] || new Set();

      let serial = String(r.serial || '').trim();
      if (serial && used.has(serial)) serial = '';
      if (!serial) {
        const next = pool.find((s) => !used.has(s));
        if (next) serial = next;
      }
      if (serial) {
        used.add(serial);
        usedByGroup[groupKey] = used;
      }

      const mat = materialById.get(r.materialId);
      const brandFromMov = serial ? String(brandByGroupSerial[`${groupKey}|${serial}`] || '').trim() : '';
      let brandName = String(r.brandName || '').trim() || brandFromMov || (mat?.brand || '') || '';
      if (!brandName) {
        const obs = String(r.observations || '').toLowerCase();
        const m = obs.match(/marca\s+([a-z0-9- ]+)/i);
        if (m && m[1]) brandName = m[1].toUpperCase().trim();
      }
      const observations = r.observations || '';
      return { ...r, serial, brandName, observations };
    });
  }, [results, movementLoans, movementFilter, serialPoolByGroup, brandByGroupSerial, resolveDocToDni, materialById]);

  const handleConsult = (e) => {
    e?.preventDefault();
    setSelectedDoc(pendingDoc);
    if (!pendingDoc) setStatusMsg({ type: 'warn', text: 'Selecciona un responsable para consultar.' });
    else setStatusMsg(null);
  };

  const handleExportExcel = () => {
    if (!selectedDoc) {
      setStatusMsg({ type: 'warn', text: 'Selecciona un responsable antes de exportar.' });
      return;
    }
    const isAll = String(selectedDoc) === 'all';
    const byDocName = new Map(responsibles.map(r => [String(r.id), String(r.name || r.id)]));
    const detailRows = resultsEnriched.map((r) => ({
      Responsable: isAll ? (byDocName.get(String(resolveDocToDni(r.responsibleDoc))) || String(resolveDocToDni(r.responsibleDoc))) : (selectedResponsible?.name || selectedDoc),
      Documento: resolveDocToDni(r.responsibleDoc),
      Material: r.materialName || materialById.get(r.materialId)?.name || '',
      Serie: (r.serial || '').trim(),
      Marca: (r.brandName || '').trim(),
      Cantidad: r.quantity,
      Bodega: (() => {
        const widNorm = normalizeWarehouseId(r.warehouseId || '');
        return warehouseNameById.get(widNorm) || r.warehouseId || '';
      })(),
      'Fecha asignación': r.assignDate || '',
      Observaciones: r.observations || ''
    }));
    const totalsRows = totals.map((t) => ({
      Material: t.materialName || materialById.get(Number(t.materialId))?.name || '',
      'Total asignado': t.quantity
    }));

    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(detailRows);
    const ws2 = XLSX.utils.json_to_sheet(totalsRows);
    XLSX.utils.book_append_sheet(wb, ws1, 'Detalle');
    XLSX.utils.book_append_sheet(wb, ws2, 'Totales');
    const filename = `consulta_responsable_${isAll ? 'todos' : String(selectedDoc).replace(/[^\w-]/g, '')}.xlsx`;
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'almacen', reportName: 'Consulta por responsable' });
    XLSX.writeFile(wb, filename);
    setStatusMsg({ type: 'ok', text: 'Archivo Excel generado.' });
  };

  const handlePrint = () => {
    if (!selectedDoc) {
      setStatusMsg({ type: 'warn', text: 'Selecciona un responsable antes de imprimir.' });
      return;
    }
    const respName = selectedResponsible?.name || selectedDoc;
    const ts = new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' });
    const whName = warehouseFilter === 'all'
      ? 'Todas'
      : (warehouses.find(w => w.id === warehouseFilter)?.name || warehouseFilter);

    const detailRows = resultsEnriched.map((r) => ({
      Responsable: respName,
      Documento: resolveDocToDni(r.responsibleDoc),
      Material: r.materialName || materialById.get(r.materialId)?.name || '',
      Serie: (r.serial || '').trim(),
      Marca: (r.brandName || '').trim(),
      Cantidad: r.quantity,
      Bodega: r.warehouseId || '',
      'Fecha asignación': r.assignDate || '',
      Observaciones: r.observations || ''
    }));
    const totalsRows = totals.map((t) => ({
      Material: t.materialName || materialById.get(Number(t.materialId))?.name || '',
      'Total asignado': t.quantity
    }));

    printResponsibleSummary({
      responsibleName: respName,
      responsibleDoc: resolveDocToDni(selectedDoc),
      warehouseLabel: whName,
      generatedAt: ts,
      assignedBy: assignedBy || '',
      details: detailRows,
      totals: totalsRows
    });
  };

  return (
    <div className="text-gray-800">
      <div className="flex items-center justify-between w-full mb-4">
        <h3 className="section-title">Consulta por Responsable</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportExcel}
            className="px-3 py-2 rounded-md text-sm font-medium focus:outline-none border border-gray-300 hover:bg-gray-50"
            style={{ backgroundColor: '#fff', color: '#333' }}
          >
            Exportar Excel
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="px-3 py-2 rounded-md text-sm font-medium focus:outline-none border border-gray-300 hover:bg-gray-50"
            style={{ backgroundColor: '#fff', color: '#333' }}
          >
            Imprimir resumen
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">Responsable</label>
          <select
            value={pendingDoc}
            onChange={(e) => {
              const v = e.target.value;
              setPendingDoc(v);
              setSelectedDoc(v);
              if (!v) setStatusMsg({ type: 'warn', text: 'Selecciona un responsable para consultar.' });
              else setStatusMsg(null);
            }}
            className="border rounded px-3 py-2 text-sm w-full"
          >
            <option value="">Seleccione...</option>
            <option value="all">Todos</option>
            {responsibles.map((r) => (
              <option key={r.id} value={r.id}>{r.name} ({r.id})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Bodega</label>
          <select
            value={warehouseFilter}
            onChange={(e) => setWarehouseFilter(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-full"
          >
            <option value="all">Todas</option>
            {warehousesSource.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Categoría</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-full"
          >
            <option value="all">Todos</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tipo mov.</label>
          <select
            value={movementFilter}
            onChange={(e) => setMovementFilter(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-full"
          >
            <option value="all">Todos</option>
            <option value="asignacion">Asignación</option>
            <option value="prestamo">Préstamo</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleConsult}
            className="px-3 py-2 rounded-md text-sm font-medium focus:outline-none border border-gray-300 hover:bg-gray-50"
            style={{ backgroundColor: '#fff', color: '#333' }}
          >
            Consultar
          </button>
          {statusMsg && (
            <span className={`ml-3 text-sm ${statusMsg.type === 'warn' ? 'text-yellow-700' : 'text-green-700'}`}>{statusMsg.text}</span>
          )}
        </div>
      </div>

      {selectedDoc && (
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-semibold mb-2">Detalle de asignaciones</h4>
            <div className="overflow-auto border rounded">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-700">
                    <th className="px-3 py-2 text-left">Material</th>
                    <th className="px-3 py-2 text-left">Serie</th>
                    <th className="px-3 py-2 text-left">Marca</th>
                    <th className="px-3 py-2 text-center">Cantidad</th>
                    <th className="px-3 py-2 text-left">Bodega</th>
                    <th className="px-3 py-2 text-left">Asignado</th>
                    <th className="px-3 py-2 text-left">Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {resultsEnriched.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-3 py-4 text-center text-gray-500">Sin asignaciones activas para el responsable seleccionado.</td>
                    </tr>
                  ) : resultsEnriched.map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="px-3 py-2">{r.materialName || materialById.get(r.materialId)?.name || '—'}</td>
                      <td className="px-3 py-2">{(r.serial || '').trim() || '—'}</td>
                      <td className="px-3 py-2">{(r.brandName || '').trim() || '—'}</td>
                      <td className="px-3 py-2 text-center">{r.quantity}</td>
                      <td className="px-3 py-2">
                        {(() => {
                          const widNorm = normalizeWarehouseId(r.warehouseId || '');
                          return warehouseNameById.get(widNorm) || r.warehouseId || '—';
                        })()}
                      </td>
                      <td className="px-3 py-2">{r.assignDate || '—'}</td>
                      <td className="px-3 py-2">{r.observations || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-2">Totales por material</h4>
            <div className="overflow-auto border rounded">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-700">
                    <th className="px-3 py-2 text-left">Material</th>
                    <th className="px-3 py-2 text-center">Total Asignado</th>
                  </tr>
                </thead>
                <tbody>
                  {totals.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-3 py-4 text-center text-gray-500">Sin datos.</td>
                    </tr>
                  ) : totals.map((t) => (
                    <tr key={t.materialId} className="border-t">
                      <td className="px-3 py-2">{t.materialName || materialById.get(Number(t.materialId))?.name || '—'}</td>
                      <td className="px-3 py-2 text-center">{t.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
